import { Card, Muted, Screen, Stack, color, radius, space, status } from '@biopeak/ui';
import { useMemo, useState } from 'react';
import { formatElapsed } from '../lib/format.ts';
import { triageCounts, useTriage, type TriageRow } from '../lib/triage.ts';

/** Situação selecionada no topo. Uma de cada vez — ver `Filtros` abaixo. */
type Filtro = 'all' | 'red' | 'yellow' | 'green' | 'stale';

const HORAS_SEM_SINCRONIZAR = 48;

/**
 * Triagem — tela inicial do painel (build-prompt §4.1).
 *
 * Único lugar do produto onde o semáforo aparece cru, de propósito: é
 * ferramenta de trabalho do médico, não do paciente (§8.3). Cada linha:
 * nome, motivo do estado atual, tempo nesse estado, último dado sincronizado.
 *
 * OS CONTADORES SÃO O FILTRO, e isso resolve uma duplicação que existia: antes
 * havia uma fila de contadores (só leitura) e, logo abaixo, uma fila de pílulas
 * de filtro com os MESMOS rótulos. O médico lia "97 vermelho" e precisava
 * procurar outro controle, com o mesmo nome, para ver quem eram. Agora o
 * número é o botão.
 *
 * FILTRO ÚNICO, não combinável. "Vermelho" e "sem sincronizar há 48h+" são
 * perguntas diferentes sobre a mesma lista, e na triagem o médico faz uma de
 * cada vez — quem precisa cruzar as duas tem a busca por nome ao lado. Combinar
 * traria o estado vazio silencioso ("nenhum paciente nesse filtro") sem que a
 * interface deixe claro que dois filtros estão ligados.
 */
export function Triage({ onOpenPatient }: { onOpenPatient: (patientId: string) => void }) {
  const triage = useTriage();
  const [filtro, setFiltro] = useState<Filtro>('all');
  const [busca, setBusca] = useState('');

  const rows = triage.data ?? [];
  const counts = useMemo(() => triageCounts(rows), [rows]);

  const semSincronizar = useMemo(
    () => rows.filter((row) => estaSemSincronizar(row)).length,
    [rows],
  );

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (filtro === 'stale') {
        if (!estaSemSincronizar(row)) return false;
      } else if (filtro !== 'all') {
        if ((row.status ?? 'green') !== filtro) return false;
      }
      return bateNaBusca(row, busca);
    });
  }, [rows, filtro, busca]);

  return (
    <Screen style={{ maxWidth: '1100px' }}>
      <Stack gap={space.xs}>
        <h1>Triagem</h1>
        <Muted>Seus pacientes, do que precisa de atenção primeiro.</Muted>
      </Stack>

      <div
        style={{
          display: 'grid',
          // `auto-fit` com mínimo: cinco colunas na tela cheia, e quebra
          // sozinho em telas estreitas sem media query nenhuma.
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: space.sm,
        }}
      >
        <CardFiltro
          rotulo="Total de pacientes"
          contagem={rows.length}
          ativo={filtro === 'all'}
          onClick={() => setFiltro('all')}
        />
        <CardFiltro
          rotulo="Vermelho"
          contagem={counts.red}
          tom="red"
          ativo={filtro === 'red'}
          onClick={() => setFiltro('red')}
        />
        <CardFiltro
          rotulo="Amarelo"
          contagem={counts.yellow}
          tom="yellow"
          ativo={filtro === 'yellow'}
          onClick={() => setFiltro('yellow')}
        />
        <CardFiltro
          rotulo="Verde"
          contagem={counts.green}
          tom="green"
          ativo={filtro === 'green'}
          onClick={() => setFiltro('green')}
        />
        <CardFiltro
          rotulo={`Sem sincronizar há ${HORAS_SEM_SINCRONIZAR}h+`}
          contagem={semSincronizar}
          ativo={filtro === 'stale'}
          onClick={() => setFiltro('stale')}
        />
      </div>

      <BuscaPorNome valor={busca} onChange={setBusca} />

      {triage.isLoading && <Muted>Carregando…</Muted>}

      {triage.isError && (
        <Muted style={{ color: status.red.fg }}>
          Não foi possível carregar a triagem agora. Tente novamente em instantes.
        </Muted>
      )}

      {triage.data && filtered.length === 0 && (
        <Card>
          <Muted>
            {rows.length === 0
              ? 'Nenhum paciente vinculado a você ainda.'
              : busca
                ? `Nenhum paciente com "${busca}" nessa situação.`
                : 'Nenhum paciente nessa situação.'}
          </Muted>
        </Card>
      )}

      <Stack gap={space.sm}>
        {filtered.map((row) => (
          <TriageRowCard
            key={row.patient_id}
            row={row}
            onOpen={() => row.patient_id && onOpenPatient(row.patient_id)}
          />
        ))}
      </Stack>
    </Screen>
  );
}

function estaSemSincronizar(row: TriageRow): boolean {
  const limite = HORAS_SEM_SINCRONIZAR * 3600_000;
  const ultimo = row.last_sync_at ? new Date(row.last_sync_at).getTime() : null;
  // Nunca sincronizou conta como sem sincronizar: para o médico, "não temos
  // dado nenhum" é o mesmo problema de "o dado está velho", e pior.
  return ultimo === null || Date.now() - ultimo > limite;
}

/** Sem acento e minúsculo, para "sheilla" achar "Sheilla" e "leao" achar "Leão". */
function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

function bateNaBusca(row: TriageRow, busca: string): boolean {
  if (!busca.trim()) return true;
  return normalizar(row.full_name ?? '').includes(normalizar(busca));
}

/**
 * Contador que também é filtro.
 *
 * COR COM PARCIMÔNIA, mesmo aqui. O semáforo aparece como uma bolinha e como
 * um filete no topo do card — não como fundo saturado. Cinco blocos de cor
 * cheia lado a lado competiriam entre si e com a lista, e a tela toda passaria
 * a gritar; o estado que precisa gritar é o da LINHA do paciente, não o do
 * contador. Selecionado se marca pelo ouro da marca, o mesmo que todo o resto
 * do produto usa para "isto está ativo".
 */
function CardFiltro({
  rotulo,
  contagem,
  tom,
  ativo,
  onClick,
}: {
  rotulo: string;
  contagem: number;
  tom?: 'red' | 'yellow' | 'green';
  ativo: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        padding: `${space.sm} ${space.md}`,
        borderRadius: radius.card,
        background: color.white,
        border: `1px solid ${ativo ? color.accent : color.border}`,
        // Segundo sinal do selecionado, além da borda: contorno não é
        // suficiente para quem enxerga pouco contraste.
        boxShadow: ativo ? `inset 0 0 0 1px ${color.accent}` : 'none',
        borderTop: tom ? `3px solid ${status[tom].fg}` : `1px solid ${ativo ? color.accent : color.border}`,
      }}
    >
      <Stack gap="2px">
        <div style={{ display: 'flex', alignItems: 'center', gap: space.xs }}>
          {tom && <StatusDot tone={tom} />}
          <span style={{ fontSize: '24px', fontWeight: 600, letterSpacing: '-0.01em' }}>
            {contagem}
          </span>
        </div>
        <Muted style={{ fontSize: '13px' }}>{rotulo}</Muted>
      </Stack>
    </button>
  );
}

function BuscaPorNome({
  valor,
  onChange,
}: {
  valor: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: space.md,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          color: color.textSecondary,
        }}
      >
        <LupaIcon />
      </span>
      <input
        type="search"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Buscar por nome"
        aria-label="Buscar paciente por nome"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: `12px ${space.md} 12px 44px`,
          fontSize: '15px',
          font: 'inherit',
          fontFamily: 'inherit',
          color: color.text,
          background: color.white,
          border: `1px solid ${color.border}`,
          borderRadius: radius.card,
          outline: 'none',
        }}
      />
    </div>
  );
}

/** Glifo inline: não há pacote de ícones no repo, e um glifo não justifica um. */
function LupaIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
      <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function OlhoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

/** Bolinha vermelha com número — mensagem do paciente ainda não lida. */
function UnreadBadge({ count }: { count: number }) {
  return (
    <span
      aria-label={count === 1 ? '1 mensagem não lida' : `${count} mensagens não lidas`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '18px',
        height: '18px',
        padding: '0 5px',
        borderRadius: radius.pill,
        background: '#C7332B',
        color: color.white,
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/**
 * Pill "N novo(s)" — chegou exame que o médico ainda não abriu a aba pra ver.
 *
 * Cor da marca, não vermelho: vermelho aqui já significa "o motor de alerta
 * notificou" (§3.1/§10). Um exame novo não é gravidade clínica, é só "tem
 * algo esperando" — mesma distinção que já existe pro comunicado não lido.
 */
function NovoExameBadge({ count }: { count: number }) {
  return (
    <span
      aria-label={count === 1 ? '1 exame novo' : `${count} exames novos`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding: `1px ${space.xs}`,
        borderRadius: radius.pill,
        background: `${color.accent}18`,
        color: color.accent,
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: 1.4,
        flexShrink: 0,
      }}
    >
      {count === 1 ? 'exame novo' : `${count} exames novos`}
    </span>
  );
}

function StatusDot({ tone }: { tone: 'red' | 'yellow' | 'green' }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: status[tone].fg,
        flexShrink: 0,
      }}
    />
  );
}

const STATUS_LABEL: Record<'red' | 'yellow' | 'green', string> = {
  red: 'Vermelho',
  yellow: 'Amarelo',
  green: 'Verde',
};

/**
 * Selo do estado na linha do paciente.
 *
 * Fundo suave + texto escuro dos tokens `status`, não bloco de cor saturada:
 * numa lista de 133 linhas, pílulas berrantes empilhadas viram um paredão que
 * cansa antes de informar. O contraste do texto sobre o fundo claro é o que
 * carrega a leitura; a saturação fica reservada para a bolinha, que é pequena
 * e some no meio do resto.
 */
function StatusPill({ tone }: { tone: 'red' | 'yellow' | 'green' }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: `3px ${space.sm}`,
        borderRadius: radius.pill,
        background: status[tone].bg,
        color: status[tone].fg,
        border: `1px solid ${status[tone].border}`,
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
      }}
    >
      {STATUS_LABEL[tone]}
    </span>
  );
}

function TriageRowCard({ row, onOpen }: { row: TriageRow; onOpen: () => void }) {
  const tone = (row.status ?? 'green') as 'red' | 'yellow' | 'green';
  const unread = row.unread_patient_messages ?? 0;
  const novosExames = row.new_documents ?? 0;

  return (
    // `button` de verdade, não `div` com onClick: a triagem é a tela de
    // trabalho do médico e precisa ser percorrível por teclado.
    //
    // O "Ver" à direita é AFORDÂNCIA VISUAL, não um segundo controle: um
    // `button` dentro de outro `button` é HTML inválido e faz o leitor de tela
    // anunciar dois alvos para a mesma ação. A linha inteira já é o alvo, que
    // no desktop é bem maior e mais fácil de acertar que um link de 40px.
    <button
      onClick={onOpen}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      <Card compact>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: space.md,
            flexWrap: 'wrap',
          }}
        >
          <Stack gap={space.xs} style={{ minWidth: '200px', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
              <StatusDot tone={tone} />
              <span style={{ fontSize: '17px', fontWeight: 600 }}>{row.full_name}</span>
              {row.is_manual_override && (
                <span style={{ fontSize: '12px', color: color.textSecondary }}>· manual</span>
              )}
              {unread > 0 && <UnreadBadge count={unread} />}
              {novosExames > 0 && <NovoExameBadge count={novosExames} />}
            </div>
            <Muted style={{ fontSize: '13px' }}>
              {row.reason ?? `Sem alteração — ${STATUS_LABEL[tone].toLowerCase()} desde o cadastro`}
            </Muted>
          </Stack>

          <div style={{ display: 'flex', alignItems: 'center', gap: space.lg, flexShrink: 0 }}>
            <StatusPill tone={tone} />

            <span
              aria-hidden
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: space.xs,
                color: color.accent,
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              <OlhoIcon />
              Ver
            </span>

            <Stack gap="2px" style={{ alignItems: 'flex-end' }}>
              <Muted style={{ fontSize: '12px', textAlign: 'right' }}>
                Nesse estado {formatElapsed(row.entered_state_at)}
              </Muted>
              <Muted style={{ fontSize: '12px', textAlign: 'right' }}>
                Sincronizou {formatElapsed(row.last_sync_at)}
              </Muted>
            </Stack>
          </div>
        </div>
      </Card>
    </button>
  );
}
